import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { tableRowLab } from "../../../style/LapPageStyle";
import useDebounce from "../../../hooks/UseDebounce";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomModal from "../../CustomModal/CustomModal";
import CloseIcon from "@mui/icons-material/Close";
import {
  autoFocusOffWithOutBorder,
  tableCellNewPrescription,
} from "../../../style/PatinetFormStyle";
import { menuItemStyle } from "../../../style/AppointmentStyle";
import {
  primaryBtnTeal,
  rotateSpinner,
} from "../../../style/PatientDetailsStyle";
import UseFetchDoctorList from "../../../helper/UseFetchDoctorList";
import { showToast } from "../../../utils/toastUtil";
import { postRemarks } from "../../../connections/postApi";
import ModalDelete from "./ModalDelete";
import { deleteMedicineRemarks } from "../../../connections/deleteApi";
import UseFetchMedicineRemarks from "../../../helper/UseFetchMedicineRemarks";
import { updateMedicineRemarks } from "../../../connections/putApi";

const MedicineRemarks = () => {
  const isMdUp = useMediaQuery("(min-width:450px)");
  const visiblePageCount = isMdUp ? 3 : 5;
  const [page, setPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [medicineSearchInput, setMedicineSearchInput] = useState("");
  const [newMedicine, setNewMedicine] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [lastPage, setLastPage] = useState(true);
  const [errors, setErrors] = useState(true);
  const [formState, setFormState] = useState({
    doctor_id: null,
    value: null,
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [id, setId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const { data: doctorOptions } = UseFetchDoctorList();

  const debouncedInputMedicine = useDebounce(medicineSearchInput, 1000);
  const {
    data: medicineRemarks,
    refetch,
    isLoading,
    isFetching,
  } = UseFetchMedicineRemarks(page, itemPerPage, debouncedInputMedicine);

  const totalRecords = medicineRemarks?.meta?.total_count || 0;
  const totalPage = Math.ceil(totalRecords / itemPerPage);

  // a new search always starts from page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedInputMedicine]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.value) newErrors.value = "Value is required.";
    if (!formState.doctor_id) newErrors.doctor_id = "Doctor is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if there are no errors
  };

  const handleAddRemarks = async (e) => {
    // console.log(formState, 'formState')
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    e.preventDefault();
    setLoading(true);
    const payload = {
      medicine_remark: {
        ...formState,
      },
    };
    try {
      const result = await postRemarks(payload);
      // console.log(result);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
      if (result?.status?.status === "SUCCESS") {
        setFormState({
          doctor_id: null,
          value: null,
        });
      }

      refetch();
      setNewMedicine(false);
    } catch (error) {
      showToast(error.message, "ERROR");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteModal(false);
    const res = await deleteMedicineRemarks(id);
    showToast(res?.status?.message || res?.status?.status, res?.status?.status);
    if (res?.status?.status) {
      refetch(page, itemPerPage);
    }
  };

  const handleEdit = (remark) => {
    setIsEdit(true);
    setNewMedicine(true);
    setId(remark?.id);
    setFormState({
      doctor_id: remark?.doctor_id,
      value: remark?.value,
    });
  };

  const handleUPdateRemarks = async (e) => {
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }
    e.preventDefault();
    setLoading(true);
    const payload = {
      medicine_remark: {
        ...formState,
      },
    };
    try {
      const result = await updateMedicineRemarks(payload, id);
      // console.log(result);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
      if (result?.status?.status === "SUCCESS") {
        setFormState({
          doctor_id: null,
          value: null,
        });
      }

      refetch();
      setNewMedicine(false);
    } catch (error) {
      showToast(error.message, "ERROR");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{ width: "100%", padding: "10px", backgroundColor: "#FFFFFF", px: 1 }}
    >
      <Box
        mt={1}
        mb={0}
        marginLeft={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="title_medium" sx={{ fontSize: "18px" }}>
              Medicine Remarks
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 1,
              alignItems: "center",
            }}
          >
            <AddRoundedIcon
              onClick={() => setNewMedicine(true)}
              sx={{
                backgroundColor: newMedicine ? "neutral.tealDark" : "#FFFFFF",
                p: "4px",
                width: "24px",
                color: newMedicine ? "#FFFFFF" : "neutral.teal",
                borderRadius: "10px",
                cursor: "pointer",
                border: "2px solid #007A99",
                transition: "0.3s ease",
                "&:hover": {
                  backgroundColor: "#007A99", // teal
                  color: "#FFFFFF",
                },
              }}
            />

            <IconButton
              onClick={() => refetch()}
              sx={{
                backgroundColor: "#FFFFFF",
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                cursor: "pointer",
                border: "2px solid #007A99",
                transition: "0.3s ease",

                "&:hover": {
                  backgroundColor: "neutral.teal",
                },
              }}
            >
              <CachedOutlinedIcon
                sx={{
                  width: "22px",
                  height: "22px",
                  color: "neutral.teal",
                  transition: "0.3s ease",

                  // white color on parent hover
                  ".MuiIconButton-root:hover &": {
                    color: "#FFFFFF",
                  },
                  // animation if loading
                  animation: isFetching
                    ? `${rotateSpinner} 0.9s linear infinite`
                    : "none",
                }}
              />
            </IconButton>

          </Box>
        </Box>

        {/* right side: search */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginRight: 1 }}>
          <TextField
            size="small"
            placeholder="Search remarks"
            value={medicineSearchInput}
            onChange={(e) => setMedicineSearchInput(e.target.value)}
            sx={{
              width: { xs: "160px", sm: "240px" },
              "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#F7F7FF" },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& input": { fontSize: "13px", padding: "9px 12px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "18px", color: "#7A8A90" }} />
                </InputAdornment>
              ),
              endAdornment: medicineSearchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setMedicineSearchInput("")}>
                    <CloseIcon sx={{ fontSize: "15px", color: "#7A8A90" }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>
      <Grid item xs={12} mt={3}>
        <TableContainer sx={{ mt: 1, width: "100%" }}>
          <Table sx={{ width: "100%" }}>
            <TableHead stickyHeader>
              <TableRow
                sx={{
                  alignItems: "center",
                  "& th": {
                    backgroundColor: "#EDEDED",
                  },
                }}
              >
                <TableCell sx={{ borderTopLeftRadius: "16px" }}>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    ID
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Value
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Doctor
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderTopRightRadius: "16px" }}>
                  <Typography
                    variant="title_small2"
                    color={"#333333"}
                    fontSize={"18px"}
                  >
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={12} sx={{ p: 0 }}>
                    <Box
                      sx={{
                        rowGap: 1.2,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((item, index) => (
                        <Skeleton
                          key={index}
                          variant="rectangular"
                          sx={{
                            width: "100%",
                            height: "50px",
                            backgroundColor: "#f0f0f0",
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {medicineRemarks?.payload?.medicine_remarks?.map(
                  (medicine, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        ...tableRowLab,
                        textAlign: "left",
                        backgroundColor: "neutral.white",
                        "& td:last-of-type": {
                          borderTopRightRadius: "12px",
                          borderBottomRightRadius: "12px",
                          textAlign: "right",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          ...tableCellNewPrescription,
                          borderTopLeftRadius: "20px",
                          borderBottomLeftRadius: "20px",
                          width: "20px",
                        }}
                      >
                        <Typography
                          variant="subTitle116px"
                          sx={{ color: "#3A3A3A" }}
                        >
                          {" "}
                          {medicine?.id}{" "}
                        </Typography>
                      </TableCell>

                      <TableCell sx={tableCellNewPrescription}>
                        <Typography
                          variant="subTitle116px"
                          sx={{
                            cursor: "pointer",
                            display: "inline-block",
                            py: 1,
                          }}
                        >
                          {medicine?.value}
                        </Typography>
                      </TableCell>

                      <TableCell sx={tableCellNewPrescription}>
                        <Typography
                          variant="subTitle116px"
                          sx={{
                            color: "#3A3A3A",
                            py: 1,
                          }}
                        >
                          {medicine?.doctor}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          ...tableCellNewPrescription,
                          width: "30px",
                          borderTopRightRadius: "20px",
                          borderBottomRightRadius: "20px",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: "1px" }}>
                          <IconButton onClick={() => handleEdit(medicine)}>
                            <EditNoteOutlinedIcon
                              sx={{ width: "24px", color: "#4A4A4A" }}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setDeleteModal(true);
                              setId(medicine?.id);
                            }}
                          >
                            <DeleteForeverOutlinedIcon
                              sx={{ width: "24px", color: "#FB3748" }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 0px",
          backgroundColor: "#fff",
          flexWrap: "wrap",
        }}
      >
        <Typography fontSize="14px">
          Showing {(page - 1) * itemPerPage + 1} -
          {Math.min(page * itemPerPage, totalRecords)} of {totalRecords} entries
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* Previous Button */}
          <Button
            onClick={() => page > 1 && setPage(page - 1)}
            startIcon={<ArrowBackIcon sx={{ fontSize: "14px" }} />}
            disabled={page === 1}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              color: page === 1 ? "#9e9e9e" : "#007A99",
              "&:disabled": { color: "#9e9e9e" },
            }}
          >
            Previous
          </Button>

          {/* PAGE NUMBER BUTTONS */}
          {[...Array(totalPage)].slice(0, visiblePageCount).map((_, idx) => {
            const pageNum = idx + 1;
            return (
              <Button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                sx={{
                  minWidth: "30px",
                  height: "30px",
                  fontSize: "12px",
                  borderRadius: "6px",
                  color: page === pageNum ? "#fff" : "#333",
                  backgroundColor: page === pageNum ? "#007A99" : "#fff",
                  border: "1px solid #d0d0d0",
                  "&:hover": {
                    backgroundColor: page === pageNum ? "#336CFB" : "#f2f2f2",
                  },
                }}
              >
                {pageNum}
              </Button>
            );
          })}

          {page > 5 && (
            <Button
              // onClick={() => setPage(pageNum)}
              sx={{
                minWidth: "30px",
                height: "30px",
                fontSize: "12px",
                borderRadius: "6px",
                color: "#fff",
                backgroundColor: "#007A99",
                border: "1px solid #d0d0d0",
                "&:hover": {
                  backgroundColor: "#007A99",
                },
              }}
            >
              {page}
            </Button>
          )}

          {/* Next Button */}
          <Button
            onClick={() => page < totalPage && setPage(page + 1)}
            endIcon={<ArrowForwardIcon sx={{ fontSize: "14px" }} />}
            disabled={page === totalPage}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              color: page === totalPage ? "#9e9e9e" : "#007A99",
              "&:disabled": { color: "#9e9e9e" },
            }}
          >
            Next
          </Button>

          {/* Last Button */}
          {page !== totalPage && (
            <Button
              onClick={() => setPage(totalPage)}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#336CFB",
              }}
              // disabled={page === totalPage}
            >
              Last &gt;&gt;
            </Button>
          )}

          {/* First  Button */}

          {page !== 1 && (
            <Button
              onClick={() => setPage(1)}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                color: "#336CFB",
              }}
              disabled={page === 1}
            >
              &lt;&lt; First
            </Button>
          )}
        </Box>
      </Box>

      {/* modals */}
      <CustomModal
        customStyles={{ border: "none", width: "66%", marginTop: "140px" }}
        open={newMedicine}
        onClose={() => {
          setNewMedicine(false);
          setFormState({
            doctor_id: null,
            value: null,
          });
          setIsEdit(false);
        }}
      >
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
            position="sticky"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography
                variant="title_medium"
                gutterBottom
                sx={{ fontSize: "20px" }}
              >
                {isEdit ? "Update Medicine Remarks" : "Add Medicine Remarks"}
              </Typography>
            </Box>
            <IconButton
              sx={{ marginBottom: "r0em" }}
              onClick={() => {
                setNewMedicine(false);
                setFormState({
                  doctor_id: null,
                  value: null,
                });
                setIsEdit(false);
              }}
            >
              <CloseIcon size="small" sx={{ color: "red" }} />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            <Grid item sm={6}>
              <Autocomplete
                clearOnEscape={false}
                clearIcon={null}
                sx={{
                  ...autoFocusOffWithOutBorder,
                  backgroundColor: "neutral.white",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "50px",
                    padding: "9px !important",
                    "& input": {
                      padding: "8px",
                      fontSize: "14px",
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      display: "none",
                    },
                  },
                }}
                name="doctor_id"
                options={
                  doctorOptions?.doctors?.filter((option) => option) || []
                }
                getOptionLabel={(option) => option?.full_name || ""}
                value={
                  doctorOptions?.doctors?.find(
                    (option) => option.id === formState.doctor_id,
                  ) || null
                }
                onChange={(e, value) => {
                  setFormState((prev) => ({
                    ...prev,
                    doctor_id: value?.id || "",
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    doctor_id: undefined,
                  }));
                }}
                id="doctor"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Doctor"
                    error={!!errors.doctor_id}
                    helperText={errors["doctor"]}
                  />
                )}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option.id}
                    sx={{ ...menuItemStyle }}
                  >
                    {option.full_name}
                  </MenuItem>
                )}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
              />
            </Grid>

            <Grid item sm={6}>
              <TextField
                value={formState?.value}
                fullWidth
                placeholder="Value"
                name="value"
                onChange={(e) => handleFormChange(e)}
                sx={{
                  ...autoFocusOffWithOutBorder,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "50px",
                    padding: "9px !important",
                    "& input": {
                      padding: "8px",
                      fontSize: "14px",
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      display: "none",
                    },
                  },
                }}
                error={!!errors["value"]}
                helperText={errors["value"]}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              marginTop: 3,
              px: 1,
              width: "100%",
              display: "flex",
              justifyContent: "flex-end", // <-- This aligns the button to the right
            }}
          >
            <Button
              sx={{ ...primaryBtnTeal }}
              onClick={isEdit ? handleUPdateRemarks : handleAddRemarks}
            >
              {/* <Typography variant="heading2_14" color={"neutral.white"}> */}
              {isEdit ? "Update" : "Add"}
              {/* </Typography> */}
            </Button>
          </Box>
        </Box>
      </CustomModal>

      <ModalDelete
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        handleDelete={handleDelete}
        id={id}
        label={"Medicine Remarks"}
      />
    </Box>
  );
};

export default MedicineRemarks;
